import { firstVisible, whenElementVisible, escapeHtml, accentedHtml, dropStaleHighlights } from './helpers';

// Walks the Effectiveness table: the two tabs, the sortable headings, a row and each of its metrics, then the
// filter sidebar. Copy and order come from Tours::EffectivenessIntro; targeting lives here. Mounted manually
// by undistractors/index, so it only runs from the page title's "?".

const ROW_SELECTOR = '.undistractor-metrics-row';
// Visible at every width, so it doubles as the test for whether a row rendered its metrics at all — a row
// whose data health failed replaces all three with a notice.
const METRIC_PROBE = '.undistractor-distraction-reduced';

// Keyed by Tours::EffectivenessIntro's step key. `scoped` steps resolve inside one row rather than the
// document, so the walk across the metrics stays on the row the row step introduced.
const TARGETS = {
  tabs: { selector: '.undistractor-tab-nav', side: 'bottom', align: 'start' },
  sorting: { selector: '.sort-cell', side: 'bottom', align: 'start' },
  row: { selector: ROW_SELECTOR, side: 'bottom', align: 'start' },
  reduction: { selector: METRIC_PROBE, side: 'bottom', align: 'center', scoped: true },
  saved: { selector: '.undistractor-hours-saved', side: 'bottom', align: 'center', scoped: true },
  enabled: { selector: '.undistractor-time-enabled', side: 'bottom', align: 'center', scoped: true },
  active: { selector: '.key-label', side: 'bottom', align: 'end', scoped: true },
  filters: { selector: '#rightbar', side: 'left', align: 'start' },
};

// The active tab pane is the only one with a layout box, so this follows whichever tab the visitor is on
// without the tour having to touch the tabs.
const metricRow = () => [...document.querySelectorAll(ROW_SELECTOR)].find((row) => row.offsetParent !== null && row.querySelector(METRIC_PROBE)) ?? null;

const resolve = (key) => {
  const target = TARGETS[key];
  if (!target) return null;
  if (!target.scoped) return firstVisible(target.selector);

  const row = metricRow();
  return row ? firstVisible(target.selector, row) : null;
};

export default {
  mount({ start, payload }) {
    const steps = (payload.steps || []).filter(({ key }) => TARGETS[key]);
    if (!steps.length) return undefined;

    const stepFor = (step) => ({
      // A function, not a node: which element a key lands on follows the active tab and the breakpoint, both
      // of which can move between the filter below and the step being reached.
      element: () => resolve(step.key),
      popover: {
        title: escapeHtml(step.title),
        description: accentedHtml(step.description),
        side: TARGETS[step.key].side,
        align: TARGETS[step.key].align,
      },
      onHighlightStarted: (element) => dropStaleHighlights(element),
    });

    // Filtered up front: driver.js centres a step whose element resolves null, leaving copy about a column
    // this visitor hasn't got.
    const begin = () => {
      const present = steps.filter(({ key }) => resolve(key));
      if (!present.length) return;

      start({ steps: present.map(stepFor) });
    };

    return whenElementVisible(steps.map(({ key }) => TARGETS[key].selector).join(', '), begin);
  },
};
